/**
 * The plan being assigned, and whether it may be assigned at all.
 *
 * ── The preview is drawn from the tree the BACKEND will build the schedule from ──────────────
 * Two kinds of plan reach this dialog, and the invariant is the same for both: the calendar the
 * trainer reviews is computed from exactly the tree the server is going to schedule.
 *
 * · A STORED library entry is assigned as it stands: `PlanAssignmentProcessor.AssignAsync` deep-
 *   copies the row the backend holds, so the preview reads the raw `PlanTemplateFullResponse`, and
 *   the draft's dirty flag CLOSES the door rather than being quietly ignored — the trainer's unsaved
 *   edits are not in that row, and a preview built from the draft would show a schedule the client
 *   is never given.
 * · An UNSAVED plan (training-plan R13's `CopyOfTemplate` and `Authored` arms) carries its whole
 *   tree in the request, so the preview reads THAT BODY — the very object the POST will carry, built
 *   once by `draftToPostRequest` when the dialog opens. Amended 23 September 2026, when the web began
 *   assigning plans the trainer has not saved.
 *
 * `toScheduleTree` reads both through one structural shape, `ScheduleSource`, which the response and
 * the request both satisfy. That is also why this module reads the generated DTOs directly: `model/`
 * is where knowing the wire is the job (the seam lint bans it under `ui/`, not here).
 *
 * ── Ported from `move-with-me-frontend/.../plans/assign/model/mapTemplateToAssignInput.ts` ───
 * The projection is mobile's, field for field, including the null coalescing that makes the tree
 * total — a phase, day or session must never reach the schedule maths half-built. Two changes:
 *
 * 1. **The source is R13's three arms, not mobile's old `dedicated` endpoint.** Mobile's
 *    `AssignPlanInput` once carried an unsaved tree to `POST /plan-assignment/dedicated`; both
 *    clients now send it to `POST /plan-assignment` with a declared origin (`AssignSource`).
 * 2. **`assignability` is added.** Mobile reaches this flow from a library row that is assignable by
 *    construction; here the entry point is a button on a builder that may be showing a fork, an
 *    empty plan or unsaved work, so the refusal has to be a decision with a name and a test.
 *
 * No React, no DOM, no query hook, no i18next: this is `model/`. Refusals leave as KEYS.
 */

import type {
  TrainWithMeModulesPlansDtosRequestsPostPlanTemplateRequest as PostPlanTemplateRequest,
  TrainWithMeModulesPlansDtosResponsesPlanTemplateFullResponse as PlanTemplateFullResponse,
} from '~/shared/api/generated/model';

import { schedulingModeIs, type PlanOrigin } from '~/entities/plan-draft';

import type { ScheduleDay, SchedulePhase, ScheduleSession, ScheduleTree } from './assignSchedule';

/**
 * Where the plan being assigned came from — training-plan R13's three declarations, and what each
 * one is allowed to travel with (`assignRequest.ts` owns the body).
 *
 * · `library` — a stored library entry, given as it stands. The server forks the row; no tree.
 * · `copyOf` — an unsaved copy of a named library template, however heavily edited. The tree.
 * · `authored` — an unsaved plan started from nothing. The tree, and NO template id at all.
 */
export type AssignSource =
  | { readonly kind: 'library'; readonly templateId: number }
  | { readonly kind: 'copyOf'; readonly templateId: number; readonly plan: PostPlanTemplateRequest }
  | { readonly kind: 'authored'; readonly plan: PostPlanTemplateRequest };

/** The plan this flow assigns: where it came from, its name, and the structure the maths walks. */
export interface AssignTarget {
  readonly source: AssignSource;
  /** Display name. DATA, never i18n. */
  readonly name: string;
  readonly tree: ScheduleTree;
}

/**
 * What `toScheduleTree` reads — the part of a plan tree the schedule generator walks. Structural,
 * so the full read (`PlanTemplateFullResponse`) and the whole-tree request
 * (`PostPlanTemplateRequest`) both satisfy it without an adapter.
 */
interface ScheduleSource {
  readonly name?: string | null;
  readonly schedulingMode?: string | null;
  readonly phases?:
    | readonly {
        readonly order?: number;
        readonly name?: string | null;
        readonly cycleCount?: number;
        readonly days?:
          | readonly {
              readonly order?: number;
              readonly dayOfWeek?: number | null;
              readonly restDaysAfter?: number | null;
              readonly activationCycle?: number | null;
              readonly retirementCycle?: number | null;
              readonly excludedCycles?: readonly number[] | null;
              readonly name?: string | null;
              readonly sessions?:
                | readonly { readonly order?: number; readonly name?: string | null }[]
                | null;
            }[]
          | null;
      }[]
    | null;
}

/**
 * The backend serialises `schedulingMode` as a PascalCase string, and its enum has exactly two
 * members: `'DateBased'` and `'SelfPaced'` (`self-paced-plan-requirements.md` R1). Both are matched
 * exactly and alone — a new mode, a null, a casing change all answer `null`, the same conservative
 * allow-list `pages/plan-library`'s `schedulingMarkerOf` takes.
 *
 * The default used to be `'DateBased'`, on the reasoning that an unnamed mode still has weekdays on
 * its days. That was true while the only alternative was `SequenceBased` and is not a bet worth
 * carrying forward: a mode this build has never heard of would be handed the weekday formula, a date
 * grid and a start-date step, and the trainer would review a calendar of dates the backend is not
 * going to generate. `buildScheduleCycles` refuses it instead.
 */
function schedulingModeOf(mode: string | null | undefined): ScheduleTree['schedulingMode'] {
  // `schedulingModeIs` rather than two inline `===`: this file, the calendar's seam, the library's
  // marker and the client detail's fold all asked the same question, and two of the four asked it
  // case-insensitively. One predicate, one answer — see its own note in `model/planEnums`.
  if (schedulingModeIs(mode, 'date')) return 'DateBased';
  if (schedulingModeIs(mode, 'selfpaced')) return 'SelfPaced';
  return null;
}

/** Project a plan tree down to the structural slice, coalescing every null so the tree is total. */
function toScheduleTree(template: ScheduleSource): ScheduleTree {
  const phases: SchedulePhase[] = (template.phases ?? []).map((phase) => ({
    order: phase.order ?? 0,
    name: phase.name ?? '',
    cycleCount: phase.cycleCount ?? 1,
    days: (phase.days ?? []).map<ScheduleDay>((day) => ({
      order: day.order ?? 0,
      dayOfWeek: day.dayOfWeek ?? null,
      restDaysAfter: day.restDaysAfter ?? null,
      // R18's per-cycle lifespan, carried whole. Dropping it made every cycle of a phase hold the
      // identical day list, so both reviews drew a plan the backend was never going to generate —
      // and the self-paced one said in its own words that enumerating the cycles was how a trainer
      // sees where they differ. An absent bound is `null` rather than coalesced to a number,
      // because absent means "wherever the phase's own end is" and any number would narrow it.
      window: {
        activationCycle: day.activationCycle ?? null,
        retirementCycle: day.retirementCycle ?? null,
        excludedCycles: day.excludedCycles ?? [],
      },
      name: day.name?.trim() ? day.name.trim() : null,
      sessions: (day.sessions ?? []).map<ScheduleSession>((session) => ({
        order: session.order ?? 0,
        name: session.name ?? '',
      })),
    })),
  }));

  return { schedulingMode: schedulingModeOf(template.schedulingMode), phases };
}

/** A stored library entry, from its full read, as this flow's target. */
export function assignTargetOf(
  template: PlanTemplateFullResponse,
  templateId: number,
): AssignTarget {
  return {
    source: { kind: 'library', templateId },
    name: template.name ?? '',
    tree: toScheduleTree(template),
  };
}

/**
 * An UNSAVED plan, from the exact body the POST will carry, as this flow's target.
 *
 * The preview and the request read one object, so the calendar the trainer reviews cannot differ
 * from the tree the server schedules — the stored arm's invariant, kept for the arm that has no
 * stored row to read.
 */
export function unsavedAssignTargetOf(plan: PostPlanTemplateRequest, origin: PlanOrigin): AssignTarget {
  return {
    source:
      origin.kind === 'copyOf'
        ? { kind: 'copyOf', templateId: origin.templateId, plan }
        : { kind: 'authored', plan },
    name: plan.name,
    tree: toScheduleTree(plan),
  };
}

/**
 * Why a plan cannot be assigned right now — as a key under `web:assign.blocked`, worded by the
 * caller.
 *
 * Ordered by what the trainer can DO about it, not by cost: `unsaved` is the one with a next step
 * on the same screen, so it is reported ahead of the structural refusals it might be masking.
 */
export type AssignBlockedReason =
  /** The plan HAS a stored row and it is not here yet — a read in flight, or one that failed. */
  | 'loading'
  /**
   * There is nothing on the server to fork. Two shapes, one sentence: a stored plan with local
   * edits, and a plan that has never been stored at all.
   */
  | 'unsaved'
  /**
   * The template is a client's FORK, not a library entry. `POST /plan-assignment` refuses one
   * outright ("only library templates can be assigned") because a fork already belongs to a client;
   * re-assigning it is not a thing the domain has.
   */
  | 'notLibrary'
  /**
   * The plan runs on a scheduling mode this build cannot name — not `'DateBased'` and not
   * `'SelfPaced'`, the two members the backend's enum has (`self-paced-plan-requirements.md` R1).
   *
   * `SelfPaced` USED to be refused here, mirroring a backend refusal
   * (`plan_self_paced_not_assignable_yet`) that no longer exists: the assignment path now accepts one
   * and takes no scheduling input at all (R11/R13). That mirror is gone with the rule behind it, and
   * the door is now held shut against unknown modes alone.
   *
   * It stays shut against those for the original reason: a schedule this app cannot compute is one it
   * must not preview. `buildScheduleCycles` answers an unnameable mode with NO occurrences, so
   * without this the review would draw an empty calendar for a plan holding four phases of work and
   * then let the trainer send it. The conservative allow-list is the point — a mode arriving in a
   * future backend must reach a refusal, never the weekday formula by default.
   */
  | 'schedulingMode'
  /** No phases, or no phase with a day that carries a session — nothing to generate. */
  | 'empty'
  /**
   * An UNSAVED plan with a phase that holds no exercise. The whole tree travels in the request and
   * the backend refuses a phase with no day carrying one — and the serialiser refuses first
   * (`DraftSerialisationError`, `emptyPhase`). Named after the phase, because on a twelve-phase
   * plan the phase is the only useful fact.
   */
  | 'emptyPhase';

/** The gate's answer. `blocked` is `null` exactly when the plan may be assigned. */
export interface Assignability {
  readonly canAssign: boolean;
  readonly blocked: AssignBlockedReason | null;
  /** The phase `emptyPhase` names. Absent on every other answer. */
  readonly phase?: string;
}

const ALLOWED: Assignability = { canAssign: true, blocked: null };

function refuse(blocked: AssignBlockedReason): Assignability {
  return { canAssign: false, blocked };
}

/**
 * May an UNSAVED plan be assigned to the client it was built for?
 *
 * Its tree is the builder's own, so the stored arm's questions do not arise: there is no row to
 * wait for or to diverge from, a plan built here is never a fork, and its mode is one the builder
 * can draw. What remains is the tree being sendable at all — every phase with a day that carries an
 * exercise, which is also what makes it generate a session.
 *
 * @param emptyPhaseName The first phase that cannot be sent, as the store's `firstEmptyPhaseName`
 *   answers it — the same predicate the serialiser refuses on, so the two cannot disagree.
 */
export function unsavedAssignability(emptyPhaseName: string | null): Assignability {
  return emptyPhaseName === null
    ? ALLOWED
    : { canAssign: false, blocked: 'emptyPhase', phase: emptyPhaseName };
}

/**
 * May this plan be assigned?
 *
 * One entry point rather than four predicates a component would combine, because the COMBINATION is
 * the decision — which refusal wins when two apply is the part a test can pin and a call site
 * cannot.
 *
 * ── A missing template has TWO causes, and they need opposite sentences ──────────────────────
 * `template` is null while a read is in flight, after one failed — and on `/plans/nowy`, where there
 * is no read at all because the plan has never been stored. This function used to answer `loading`
 * to all three, so a trainer standing on a brand-new plan was told "Plan jeszcze się wczytuje.
 * Poczekaj na jego wyświetlenie." about a plan that was never going to load, and waiting was the one
 * thing that could not help. The correct sentence already existed and was unreachable:
 * `web:assign.blocked.unsaved` says to save the plan first, which is exactly the next move.
 *
 * So the caller passes the plan's id-lessness in. `planId === null` means "there is nothing on the
 * server to fork", which is the same fact `isDirty` reports for a stored plan — hence the same
 * refusal rather than a seventh reason.
 */
export function assignability(
  template: PlanTemplateFullResponse | null | undefined,
  isDirty: boolean,
  /** The stored plan being edited, or `null` for a draft the create modal seeded. */
  planId: number | null,
): Assignability {
  if (template == null) return refuse(planId === null ? 'unsaved' : 'loading');
  // Reported ahead of the structural refusals: an unsaved plan may also be empty on the server, and
  // "save first" is the only one of the two the trainer can act on without leaving the dialog.
  if (isDirty) return refuse('unsaved');
  if (template.isLibraryEntryTemplate !== true) return refuse('notLibrary');
  // Ahead of `empty`, and the order matters: a plan on an unnameable mode may well be full of
  // training days, and telling the trainer it has no workout in it would send them to fix a plan
  // that is fine.
  if (schedulingModeOf(template.schedulingMode) === null) return refuse('schedulingMode');

  const generates = (template.phases ?? []).some(
    (phase) =>
      (phase.cycleCount ?? 1) > 0 &&
      (phase.days ?? []).some((day) => (day.sessions ?? []).length > 0),
  );
  return generates ? ALLOWED : refuse('empty');
}
